import { useState } from 'react';
import type { VisitDto } from '../api/types';
import { TYPE_COLOR, TYPE_LABEL } from '../styles/theme';
import { Avatar } from './Avatar';
import { StatusBadge } from './Badge';
import { isEmbedded, openBitrixCompany } from '../bitrix/bx24';

interface TaskCardProps {
  visit: VisitDto;
  onClick?(v: VisitDto): void;
  compact?: boolean;
}

export function TaskCard({ visit, onClick, compact }: TaskCardProps) {
  const [hovered, setHovered] = useState(false);
  const color = TYPE_COLOR[visit.type] ?? '#64748b';
  const label = TYPE_LABEL[visit.type] ?? visit.typeLabel;
  const clientName =
    visit.client?.name ??
    (visit.bitrixDealId ? `Сделка #${visit.bitrixDealId}` : null) ??
    visit.customLocation?.address ??
    '—';
  const shortDate = visit.scheduledDate.slice(5).replace('-', '.'); // MM.DD

  // Нет координат у клиента → визит не виден на карте. Кастомная локация
  // спасает (диспетчер задал точку вручную). Дежурные выезды не привязаны к
  // клиенту — для них плашка не нужна.
  const hasGeo =
    (visit.client && visit.client.latitude != null && visit.client.longitude != null) ||
    visit.customLocation != null;
  const showGeoWarning = !hasGeo && visit.client != null;
  const canOpenBitrix = isEmbedded() && visit.client?.bitrixCompanyId != null;
  return (
    <div
      onClick={() => onClick?.(visit)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: compact ? '10px 12px' : '12px 14px',
        marginBottom: 8,
        borderLeft: `3px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: hovered && onClick ? '0 3px 8px rgba(0,0,0,0.12)' : '0 1px 2px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{clientName}</div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color,
            background: color + '18',
            padding: '2px 7px',
            borderRadius: 999,
            flexShrink: 0,
            marginLeft: 6,
          }}
        >
          {label}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: compact ? 0 : 5 }}>
        🕐 {visit.scheduledStart}–{visit.scheduledEnd} · {shortDate}
      </div>
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          <Avatar initials={visit.engineer.initials} color={visit.engineer.color} size={18} />
          <span style={{ fontSize: 12, color: '#475569' }}>{visit.engineer.fullName}</span>
          <span style={{ marginLeft: 'auto' }}>
            <StatusBadge status={visit.status} />
          </span>
        </div>
      )}
      {showGeoWarning && (
        <div
          onClick={(ev) => ev.stopPropagation()}
          style={{
            marginTop: 8,
            padding: '6px 8px',
            background: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: 6,
            fontSize: 11,
            color: '#92400e',
            lineHeight: 1.35,
          }}
        >
          ⚠ Адрес клиента не указан в Битриксе. Не виден на карте.
          {canOpenBitrix && (
            <>
              {' '}
              <a
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  openBitrixCompany(visit.client!.bitrixCompanyId!);
                }}
                style={{ color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
              >
                Открыть карточку компании в Битриксе
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
